/** What the card page prints about a card's own effect, on one basis each: the odds-weighted year-end price, the figure of a tile against the same year with no card, and the one line that says what the table teaches. */
import type { KpiId, KpiSnapshot } from "@bsim/domain";
import { formatKpi, formatKpiChange, formatUsdPerShare } from "../format";
import type { RiskyChoices } from "./ownLimits";

/** The year-end share price of an answer on the one basis the row prints: the odds-weighted price when the answer carries a chance, else its price. Undefined when the answer is not priced. */
export function oddsWeightedPrice(r: RiskyChoices[number] | undefined): number | undefined {
  const c = r?.chance;
  if (c !== undefined) return c.probability * c.withUsdPerShare + (1 - c.probability) * c.withoutUsdPerShare;
  return r?.kpis?.price?.to;
}

/** A tile's figure against the same year with no card: the change, with the year-end level beside it muted. Null when no no-card reading is served: the tile then prints the year-end level alone. */
export function tileAgainstNoCard(kpi: KpiId, level: number, noCard: KpiSnapshot | undefined): { readonly change: string; readonly level: string } | null {
  const base = noCard?.[kpi];
  return base === undefined ? null : { change: formatKpiChange(kpi, level - base, { signed: true }), level: formatKpi(kpi, level) };
}

/** "Here answer A adds $3.26 a share against leaving it open; the lowest, C, costs $0.12." from the answers' score effects; null with fewer than two priced answers or when none moves the score. */
export function learnLine(effects: readonly { readonly letter: string; readonly delta: number | undefined }[], against: string): string | null {
  const priced = effects.flatMap((e) => (e.delta === undefined ? [] : [{ letter: e.letter, delta: e.delta }]));
  if (priced.length < 2) return null;
  const best = priced.reduce((a, b) => (b.delta > a.delta ? b : a));
  const worst = priced.reduce((a, b) => (b.delta < a.delta ? b : a));
  const costs = worst.delta <= -0.005;
  const gains = best.delta >= 0.005;
  if (!gains && !costs) return null;
  const first = gains ? `Here answer ${best.letter} adds ${formatUsdPerShare(best.delta)} a share against ${against}` : `Here no answer beats ${against}`;
  return `${first}${costs ? `; the lowest, ${worst.letter}, costs ${formatUsdPerShare(Math.abs(worst.delta))}` : ""}.`;
}
